import { Bell, CheckCheck, ChevronRight, MessageCircle, RefreshCw, X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useEffect, useId, useRef, useState } from 'react'
import {
  listReplyNotifications, markReplyNotificationsRead, notificationBadge, notificationLabel, NOTIFICATION_POLL_MS,
  ReplyNotificationError, type ReplyNotification,
} from './replyNotificationClient'

type Props = {
  memberId: string
  revision: number
  onOpenConversation: (notification: ReplyNotification, signal: AbortSignal) => Promise<void>
  onError: (message: string) => void
}

export default function ReplyNotifications({ memberId, revision, onOpenConversation, onError }: Props) {
  const [open, setOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [items, setItems] = useState<ReplyNotification[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [summaryError, setSummaryError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const dialogRef = useRef<HTMLElement>(null)
  const bellRef = useRef<HTMLButtonElement>(null)
  const generationRef = useRef(0)
  const listGenerationRef = useRef(0)
  const countVersionRef = useRef(0)
  const busyRef = useRef(false)
  const listBusyRef = useRef(false)
  const listControllerRef = useRef<AbortController | null>(null)
  const mutationControllerRef = useRef<AbortController | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const navigatedRef = useRef(false)
  const titleId = useId()

  function clearChangedAccount(requestError: unknown) {
    if (!(requestError instanceof ReplyNotificationError) || !requestError.accountChanged) return
    setItems([]); setUnreadCount(0); setHasMore(false); setNextCursor(null); setLoaded(false)
  }

  useEffect(() => {
    const generation = ++generationRef.current
    setUnreadCount(0); setItems([]); setOpen(false); setError(''); setSummaryError(false)
    busyRef.current = false; listBusyRef.current = false
    setBusy(false); setLoading(false); setLoaded(false); setNextCursor(null); setHasMore(false)
    let activeController: AbortController | null = null
    let polling = false
    const refreshCount = async () => {
      if (document.visibilityState === 'hidden' || polling) return
      polling = true
      const controller = new AbortController()
      activeController = controller
      const countVersion = countVersionRef.current
      try {
        const page = await listReplyNotifications({ summary: true, signal: controller.signal, expectedMemberId: memberId })
        if (generation !== generationRef.current || controller.signal.aborted || countVersion !== countVersionRef.current) return
        setUnreadCount(page.unread_count); setSummaryError(false)
      } catch (requestError) {
        if (generation === generationRef.current && !controller.signal.aborted) { clearChangedAccount(requestError); setSummaryError(true) }
      } finally { polling = false }
    }
    void refreshCount()
    const interval = window.setInterval(() => { void refreshCount() }, NOTIFICATION_POLL_MS)
    const onFocus = () => { void refreshCount() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      ++generationRef.current; ++listGenerationRef.current
      activeController?.abort(); listControllerRef.current?.abort(); mutationControllerRef.current?.abort()
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [memberId, revision])

  async function loadPage(before: string | null) {
    if (listBusyRef.current || busyRef.current) return
    const generation = generationRef.current
    const listGeneration = ++listGenerationRef.current
    const countVersion = countVersionRef.current
    const controller = new AbortController()
    listControllerRef.current?.abort(); listControllerRef.current = controller
    listBusyRef.current = true; setLoading(true); setError('')
    try {
      const page = await listReplyNotifications({ before, signal: controller.signal, expectedMemberId: memberId })
      if (generation !== generationRef.current || listGeneration !== listGenerationRef.current || controller.signal.aborted) return
      setItems((current) => [...new Map((before ? [...current, ...page.notifications] : page.notifications).map((item) => [item.id, item])).values()])
      if (countVersion === countVersionRef.current) setUnreadCount(page.unread_count)
      setNextCursor(page.next_cursor); setHasMore(page.has_more); setLoaded(true); setSummaryError(false)
    } catch (requestError) {
      if (generation === generationRef.current && listGeneration === listGenerationRef.current && !controller.signal.aborted) {
        clearChangedAccount(requestError)
        setError(requestError instanceof Error ? requestError.message : 'Notifications could not load.')
      }
    } finally {
      if (listGeneration === listGenerationRef.current) { listBusyRef.current = false; setLoading(false) }
    }
  }

  useEffect(() => {
    if (!open) return
    listBusyRef.current = false; setLoaded(false); setItems([]); setNextCursor(null); setHasMore(false)
    void loadPage(null)
    return () => {
      ++listGenerationRef.current; listControllerRef.current?.abort(); listBusyRef.current = false
    }
    // Opening or an explicit refresh starts a new cursor sequence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshKey])

  useEffect(() => {
    if (!open) return
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : bellRef.current
    const priorOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation()
        if (!busyRef.current) setOpen(false)
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex="0"]') ?? [])]
        .filter((element) => element.offsetParent !== null)
      const first = focusable[0], last = focusable[focusable.length - 1]
      if (!first) { event.preventDefault(); dialogRef.current?.focus() }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) { event.preventDefault(); first.focus() }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = priorOverflow
      if (!navigatedRef.current) previousFocusRef.current?.focus()
    }
  }, [open])

  async function markAllRead() {
    if (busyRef.current || listBusyRef.current) return
    const generation = generationRef.current
    const controller = new AbortController()
    mutationControllerRef.current = controller
    busyRef.current = true; setBusy(true); setError(''); ++countVersionRef.current
    try {
      const result = await markReplyNotificationsRead({ all: true }, controller.signal, fetch, memberId)
      if (generation !== generationRef.current || controller.signal.aborted) return
      setUnreadCount(result.unread_count)
      setItems((current) => current.map((item) => ({ ...item, read_at: item.read_at ?? new Date().toISOString() })))
    } catch (requestError) {
      if (generation === generationRef.current && !controller.signal.aborted) { clearChangedAccount(requestError); setError(requestError instanceof Error ? requestError.message : 'Read status could not be saved.') }
    } finally { if (generation === generationRef.current) { busyRef.current = false; setBusy(false) } }
  }

  async function openItem(item: ReplyNotification) {
    if (busyRef.current || listBusyRef.current) return
    const generation = generationRef.current
    const controller = new AbortController()
    mutationControllerRef.current = controller
    busyRef.current = true; setBusy(true); setError('')
    try {
      await onOpenConversation(item, controller.signal)
      if (generation !== generationRef.current || controller.signal.aborted) return
      navigatedRef.current = true
      setOpen(false)
      if (!item.read_at) {
        ++countVersionRef.current
        try {
          const result = await markReplyNotificationsRead({ ids: [item.id] }, controller.signal, fetch, memberId)
          if (generation === generationRef.current && !controller.signal.aborted) setUnreadCount(result.unread_count)
        } catch (readError) {
          if (generation === generationRef.current && !controller.signal.aborted) { clearChangedAccount(readError); onError(readError instanceof Error ? readError.message : 'The conversation opened, but its notification could not be marked read.') }
        }
      }
    } catch (requestError) {
      if (generation === generationRef.current && !controller.signal.aborted) setError(requestError instanceof Error ? requestError.message : 'That conversation is unavailable.')
    } finally { if (generation === generationRef.current) { busyRef.current = false; setBusy(false) } }
  }

  return <>
    <button ref={bellRef} className="icon-button notification-bell" type="button" aria-label={notificationLabel(unreadCount)}
      disabled={busy}
      aria-haspopup="dialog" aria-expanded={open} title={summaryError ? 'Notifications could not refresh. Open to retry.' : 'Your reply notifications'}
      onClick={() => { navigatedRef.current = false; setOpen(true) }}>
      <Bell size={20} />{unreadCount > 0 && <span className="notification-badge" aria-hidden="true">{notificationBadge(unreadCount)}</span>}
    </button>
    {open && createPortal(<div className="reply-notifications-backdrop" onClick={(event) => { if (event.target === event.currentTarget && !busyRef.current) setOpen(false) }}>
      <section ref={dialogRef} className="reply-notifications-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <button className="reply-notifications-close" type="button" aria-label="Close notifications" disabled={busy} onClick={() => setOpen(false)}><X size={20} /></button>
        <header><span className="eyebrow">THE CONVERSATION CONTINUES</span><h2 id={titleId}>Notifications</h2><p>Replies to your posts and comments, all in one place.</p></header>
        <div className="reply-notifications-tools">
          <button type="button" disabled={loading || busy} onClick={() => setRefreshKey((current) => current + 1)}><RefreshCw size={15} />Refresh</button>
          <button type="button" disabled={!unreadCount || loading || busy} onClick={() => { void markAllRead() }}><CheckCheck size={16} />Mark all read</button>
          <span role="status" aria-live="polite">{loading && !loaded ? 'Checking…' : unreadCount ? `${unreadCount} unread` : 'All read'}</span>
        </div>
        {error && <div className="reply-notifications-error" role="alert">{error}<button type="button" disabled={loading || busy} onClick={() => { void loadPage(items.length && hasMore ? nextCursor : null) }}>Try again</button></div>}
        {loading && !loaded && <p className="reply-notifications-state" role="status">Loading notifications…</p>}
        {loaded && !items.length && !error && <div className="reply-notifications-empty"><MessageCircle size={30} /><h3>No replies yet</h3><p>When another fan replies, their notification will appear here.</p></div>}
        <div className="reply-notifications-list">
          {items.map((item) => <button key={item.id} type="button" className={`reply-notification${item.read_at ? '' : ' is-unread'}`} disabled={busy || loading}
            aria-label={`${item.read_at ? '' : 'Unread: '}${item.actor.display_name} replied. ${item.reply_preview}`} onClick={() => { void openItem(item) }}>
            <span className="reply-notification-avatar">{item.actor.avatar_url ? <img src={item.actor.avatar_url} alt="" /> : item.actor.display_name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('')}</span>
            <span className="reply-notification-copy"><strong>{item.actor.display_name}<span> replied</span></strong><small>@{item.actor.handle} · {new Date(item.created_at).toLocaleString()}</small>
              <span className="reply-notification-preview">{item.reply_preview}</span><span className="reply-notification-post">On: {item.post_preview}</span>
            </span><ChevronRight size={18} />
          </button>)}
        </div>
        {hasMore && <button className="reply-notifications-load-more" type="button" disabled={busy || loading} onClick={() => { void loadPage(nextCursor) }}>{loading ? 'Loading…' : 'Load more notifications'}</button>}
        <footer>In-site alerts only. Email and phone push notifications are not enabled.</footer>
      </section>
    </div>, document.body)}
  </>
}
